import React from 'react';

type Props = {
  children: React.ReactNode;
  /** Optional custom fallback. If omitted, a default French message is shown. */
  fallback?: React.ReactNode;
  /** Called once when an error is captured (e.g. for logging/telemetry). */
  onError?: (error: Error, info: React.ErrorInfo) => void;
};

type State = {
  hasError: boolean;
  error: Error | null;
};

/**
 * Empêche un écran blanc total : si le rendu 3D (WebGL, chargement de modèle,
 * pipeline de texture) ou tout autre sous-arbre lève une exception au rendu,
 * on affiche un message de repli au lieu de planter toute l'application.
 *
 * Limite connue : les erreurs levées dans la boucle d'animation (useFrame)
 * hors du cycle de rendu React ne sont pas interceptées par React.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('[ErrorBoundary] rendu interrompu :', error, info);
    this.props.onError?.(error, info);
  }

  private handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    if (this.props.fallback) {
      return this.props.fallback;
    }

    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          width: '100%',
          height: '100%',
          minHeight: 280,
          padding: 24,
          textAlign: 'center',
          color: '#374151',
          background: '#f7f7f8',
        }}
      >
        <div style={{ fontSize: 40, lineHeight: 1 }}>🛠️</div>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 600 }}>
          L'aperçu 3D n'a pas pu se charger
        </h2>
        <p style={{ margin: 0, maxWidth: 420, fontSize: 14, color: '#6b7280' }}>
          Une erreur est survenue pendant l'affichage. Vérifiez que votre
          navigateur supporte la 3D (WebGL), puis réessayez.
        </p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '10px 20px',
            fontSize: 14,
            fontWeight: 600,
            color: '#fff',
            background: '#f97316',
            border: 'none',
            borderRadius: 8,
            cursor: 'pointer',
          }}
        >
          Recharger la page
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
